import { Typography, Box, Button, TypographyVariant, useMediaQuery, Divider } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import { OrionColors } from 'src/components/theme/orion/common/colors';
import { KeywordsBackgroundContainer } from 'src/components/common/styled/StyledFooterElements';
import { SubFooter } from '../common/SubFooter';
import { FlexBoxColumn } from '../common/FlexBoxColumn';
import { StyledPageContent } from '../common/styled/StyledPageElements';
import { OrionTheme } from '../theme/orion/Theme';
import KeywordFooterImageDesktop from 'src/assets/google_ad_grader_banner_wasted_spend.png';

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  right: '72px',
  width: '450px',
  height: '100%',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'bottom right',
  zIndex: 2,
  pointerEvents: 'none',

  // TABLET / MEDIUM
  [theme.breakpoints.between('sm', 'md')]: {
    top: '50%',
    bottom: 'auto',
    transform: 'translateY(-45%)',
    right: '24px',
    width: '280px',
    height: '280px',
    backgroundPosition: 'center right',
  },

  // MOBILE
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

type Props = {
  body?: JSX.Element[];
  titleTranslationKey?: string;
  titleSize?: TypographyVariant;
  link?: string;
  withSubFooter?: boolean;
};

const renderDefaultListItem = (key: string): JSX.Element => (
  <Box key={key} component="li">
    <Typography variant="body1">
      <Trans
        ns="keywords"
        i18nKey={`footer.${key}`}
        components={[<Box key="bold" component="span" sx={{ fontWeight: 700 }} />]}
      />
    </Typography>
  </Box>
);

const getDefaultListItems = (): JSX.Element[] => [
  renderDefaultListItem('listItem1'),
  renderDefaultListItem('listItem2'),
  renderDefaultListItem('listItem3'),
];

export const Footer = ({
  body,
  titleTranslationKey = 'footer.title',
  titleSize,
  link = `https://www.wordstream.com/google-adwords?cid=Web_Any_KWT_GGLAdwords_AWGrader&utm_source=freetools&utm_medium=fkt`,
  withSubFooter = true,
}: Props): JSX.Element => {
  const { t } = useTranslation(['keywords']);
  const isMobile = useMediaQuery(OrionTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(OrionTheme.breakpoints.between('sm', 'md'));

  const handleClick = (targetLink: string): void => {
    window.open(encodeURI(targetLink), '_blank');
  };

  return (
    <>
      <KeywordsBackgroundContainer
        sx={{
          position: 'relative',
          alignSelf: 'stretch',
          overflow: 'hidden',
          height: isMobile ? '100%' : OrionTheme.spacing(58),
        }}
      >
        <ImageContainer
          sx={{
            backgroundImage: `url(${KeywordFooterImageDesktop})`,
          }}
        />

        <StyledPageContent
          sx={{
            position: 'relative',
            zIndex: 5,
            gap: 5,
            pt: isMobile ? 8 : 8,
            pb: isMobile ? 23.5 : 8,
            px: isMobile ? 1 : 0,
          }}
        >
          <FlexBoxColumn
            sx={{
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              gap: 3,
              maxWidth: isMobile ? '100%' : isTablet ? '50%' : '50%',
            }}
          >
            <Typography
              variant={titleSize ?? 'h1'}
              color={OrionColors.cream.blocking}
              sx={{
                whiteSpace: 'pre-line',
                alignSelf: 'flex-start',
                lineHeight: '35px',
              }}
            >
              <Trans
                ns="keywords"
                i18nKey={titleTranslationKey}
                components={[<i key="italic" />]}
              />
            </Typography>

            <Box
              component="ul"
              sx={{
                alignItems: 'flex-start',
                color: OrionColors.cream.blocking,
                paddingInlineStart: 3,
                my: 0,
              }}
            >
              {body ?? getDefaultListItems()}
            </Box>
          </FlexBoxColumn>

          <Button
            variant="contained"
            color="secondary"
            size="large"
            sx={{
              alignSelf: isMobile ? 'center' : 'flex-start',
              mt: isMobile ? 2 : 0,
              mb: isMobile ? 3 : 0,
              px: isMobile ? 4 : undefined,
            }}
            onClick={() => handleClick(link)}
          >
            {t('footer.label', { context: 'button' })}
          </Button>
        </StyledPageContent>
      </KeywordsBackgroundContainer>

      {withSubFooter && (
        <>
          <Divider sx={{ borderColor: OrionColors.cream.blocking }} />
          <SubFooter />
        </>
      )}
    </>
  );
};
